import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { tweetContentApi } from '../../api/bip-api/tweet-content';
import { TweetContentDto, ContentType } from '../../api/bip-api/types';
import { useToasterStore } from '../ui/toasterStore';

type ViewMode = 'list' | 'create' | 'detail';

interface TweetContentStoreState {
  // Data
  tweets: TweetContentDto[];
  selectedTweetId: string | null;

  // Loading states
  isLoading: boolean;
  isGenerating: boolean;
  isUpdating: boolean;
  isPublishing: boolean;

  // Modal state
  isModalOpen: boolean;
  modalTokenAddress: string | null;
  modalTokenSymbol: string | null;
  viewMode: ViewMode;

  // Actions
  openModal: (tokenAddress: string, tokenSymbol: string) => void;
  closeModal: () => void;
  fetchTweets: (tokenAddress: string) => Promise<void>;
  generateTweet: (
    tokenAddress: string,
    contentType: ContentType
  ) => Promise<TweetContentDto | null>;
  updateTweet: (id: string, contents: string[]) => Promise<void>;
  publishTweet: (id: string, isPersonalTweet?: boolean) => Promise<void>;
  selectTweet: (id: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
}

export const useTweetContentStore = create<TweetContentStoreState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      tweets: [],
      selectedTweetId: null,
      isLoading: false,
      isGenerating: false,
      isUpdating: false,
      isPublishing: false,
      isModalOpen: false,
      modalTokenAddress: null,
      modalTokenSymbol: null,
      viewMode: 'list',

      // Actions
      openModal: (tokenAddress: string, tokenSymbol: string) => {
        set({
          isModalOpen: true,
          modalTokenAddress: tokenAddress,
          modalTokenSymbol: tokenSymbol,
          viewMode: 'list',
          selectedTweetId: null,
          tweets: [],
        });
        // Fetch tweets when modal opens
        get().fetchTweets(tokenAddress);
      },

      closeModal: () => {
        set({
          isModalOpen: false,
          modalTokenAddress: null,
          modalTokenSymbol: null,
          viewMode: 'list',
          selectedTweetId: null,
          tweets: [],
        });
      },

      fetchTweets: async (tokenAddress: string) => {
        set({ isLoading: true });
        try {
          const tweets = await tweetContentApi.getTweetContents(tokenAddress);
          set({ tweets, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          useToasterStore.getState().showToaster(
            'Failed to load tweets',
            'error'
          );
        }
      },

      generateTweet: async (
        tokenAddress: string,
        contentType: ContentType
      ) => {
        set({ isGenerating: true });
        try {
          const newTweet = await tweetContentApi.generateTweet(
            tokenAddress,
            contentType
          );
          set((state) => ({
            tweets: [newTweet, ...state.tweets],
            selectedTweetId: newTweet.id,
            viewMode: 'detail',
            isGenerating: false,
          }));
          useToasterStore.getState().showToaster(
            'Tweet generated successfully',
            'success'
          );
          window.alert('트윗 생성이 완료되었습니다!');
          return newTweet;
        } catch (error) {
          set({ isGenerating: false });
          useToasterStore.getState().showToaster(
            'Failed to generate tweet',
            'error'
          );
          return null;
        }
      },

      updateTweet: async (id: string, contents: string[]) => {
        set({ isUpdating: true });
        try {
          const updatedTweet = await tweetContentApi.updateTweetContent(
            id,
            contents
          );
          set((state) => ({
            tweets: state.tweets.map((tweet) =>
              tweet.id === id ? updatedTweet : tweet
            ),
            isUpdating: false,
          }));
          useToasterStore.getState().showToaster(
            'Changes saved successfully',
            'success'
          );
        } catch (error) {
          set({ isUpdating: false });
          useToasterStore.getState().showToaster(
            'Failed to save changes',
            'error'
          );
        }
      },

      publishTweet: async (id: string, isPersonalTweet: boolean = false) => {
        set({ isPublishing: true });
        try {
          const publishedTweet = await tweetContentApi.publishTweet(id, isPersonalTweet);
          set((state) => ({
            tweets: state.tweets.map((tweet) =>
              tweet.id === id ? publishedTweet : tweet
            ),
            isPublishing: false,
          }));
          useToasterStore.getState().showToaster(
            'Tweet published successfully',
            'success'
          );
        } catch (error) {
          set({ isPublishing: false });
          useToasterStore.getState().showToaster(
            'Failed to publish tweet',
            'error'
          );
        }
      },

      selectTweet: (id: string | null) => {
        set({
          selectedTweetId: id,
          viewMode: id ? 'detail' : 'list',
        });
      },

      setViewMode: (mode: ViewMode) => {
        set({ viewMode: mode });
        if (mode === 'create' || mode === 'list') {
          set({ selectedTweetId: null });
        }
      },
    })),
    {
      name: 'TweetContentStore',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);
